import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "@/i18n";

const KEY = "hs_cookie_consent";

export default function CookieConsent() {
  const { t } = useI18n();
  const [banner, setBanner] = useState(false);
  const [modal, setModal] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) setBanner(true);
    else {
      try { setAnalytics(!!JSON.parse(stored).analytics); } catch {}
    }
  }, []);

  const openSettings = useCallback(() => { setModal(true); setBanner(false); }, []);
  useEffect(() => {
    window.addEventListener("hs-open-cookies", openSettings);
    return () => window.removeEventListener("hs-open-cookies", openSettings);
  }, [openSettings]);

  const persist = (analyticsVal) => {
    localStorage.setItem(KEY, JSON.stringify({ necessary: true, analytics: analyticsVal, ts: Date.now() }));
    setBanner(false);
    setModal(false);
  };

  useEffect(() => {
    document.body.style.paddingBottom = banner ? "112px" : "";
    return () => { document.body.style.paddingBottom = ""; };
  }, [banner]);

  return (
    <>
      {banner && (
        <div data-testid="cookie-banner" className="fixed bottom-0 left-0 right-0 z-[70] bg-[#0F0F0F] border-t border-white/15">
          <div className="mx-auto max-w-7xl px-6 sm:px-12 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <p className="text-[#A3A3A3] text-sm max-w-2xl">
              {t("cookie_text")}{" "}
              <Link to="/cookies" className="text-[#BC5D2E] underline">{t("legal_cookies")}</Link>
            </p>
            <div className="flex gap-3 shrink-0 flex-wrap">
              <button data-testid="cookie-customize" onClick={() => setModal(true)} className="border border-white/20 px-5 py-2 uppercase tracking-widest text-xs text-[#A3A3A3] hover:text-white hover:border-white/50 transition-colors">{t("cookie_customize")}</button>
              <button data-testid="cookie-refuse" onClick={() => persist(false)} className="border border-white/30 px-6 py-2 uppercase tracking-widest text-xs hover:border-white transition-colors">{t("cookie_refuse")}</button>
              <button data-testid="cookie-accept" onClick={() => persist(true)} className="bg-[#BC5D2E] text-white px-6 py-2 uppercase tracking-widest text-xs hover:bg-[#8A281E] transition-colors">{t("cookie_accept")}</button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70" onClick={() => setModal(false)}>
          <div data-testid="cookie-modal" className="w-full max-w-lg bg-[#0F0F0F] border border-white/15 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-clash text-2xl uppercase tracking-tight">{t("cookie_settings")}</h3>
              <button data-testid="cookie-modal-close" onClick={() => setModal(false)} className="text-[#A3A3A3] hover:text-white"><X size={20} /></button>
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex items-start justify-between border border-white/10 p-4">
                <div className="pr-4">
                  <div className="font-clash uppercase tracking-tight text-sm">{t("cookie_necessary")}</div>
                  <p className="text-[#A3A3A3] text-xs mt-1">{t("cookie_necessary_d")}</p>
                </div>
                <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E] shrink-0 mt-1">ON</span>
              </div>
              <label className="flex items-start justify-between border border-white/10 p-4 cursor-pointer">
                <div className="pr-4">
                  <div className="font-clash uppercase tracking-tight text-sm">{t("cookie_analytics")}</div>
                  <p className="text-[#A3A3A3] text-xs mt-1">{t("cookie_analytics_d")}</p>
                </div>
                <input data-testid="cookie-analytics-toggle" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1 w-5 h-5 accent-[#BC5D2E]" />
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <button data-testid="cookie-refuse-all" onClick={() => persist(false)} className="flex-1 border border-white/30 px-6 py-3 uppercase tracking-widest text-xs hover:border-white transition-colors">{t("cookie_refuse")}</button>
              <button data-testid="cookie-save" onClick={() => persist(analytics)} className="flex-1 bg-[#BC5D2E] text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] transition-colors">{t("cookie_save")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
